import { useEffect, useRef, useState } from "react";

/**
 * Parameters for the useQuery hook.
 *
 * @template TData
 */
interface IUseQueryParams<TData> {
    /**
     * Dependency array to control when the query should be refetched.
     */
    queryKey?: unknown[];
    /**
     * Function that returns a promise resolving to the query data.
     * The function receives an AbortSignal to handle cancellation.
     */
    query: (signal: AbortSignal) => Promise<TData>;
}

/**
 * Status of the query execution.
 *
 * @template TData
 * @template TError
 */
interface IQueryStatus<TData, TError> {
    /**
     * The data returned from the query.
     */
    data?: TData;
    /**
     * Indicates if the query is currently loading.
     */
    isLoading: boolean;
    /**
     * The error returned from the query.
     */
    error?: TError;
}

/**
 * Response from the useQuery hook.
 *
 * @template TData
 * @template TError
 */
interface IUseQueryResponse<TData, TError = any> {
    /**
     * The data returned from the query.
     */
    data?: TData;
    /**
     * The error returned from the query.
     */
    error?: TError;
    /**
     * Indicates if there was an error during query execution.
     */
    isError: boolean;
    /**
     * Indicates if the query is currently loading.
     */
    isLoading: boolean;
    /**
     * Indicates if the query was executed successfully.
     */
    isSuccess: boolean;
    /**
     * Function to refetch the query.
     */
    refetch: () => Promise<IQueryStatus<TData, TError>>;
}

/**
 * Custom hook to manage query execution state.
 *
 * @template TData
 * @template TError
 * @param {Readonly<IUseQueryParams<TData>>} params - The parameters for the query.
 * @returns {IUseQueryResponse<TData, TError>} The state and functions to manage query execution.
 */
function useQuery<TData, TError = any>({
    query,
    queryKey = []
}: Readonly<IUseQueryParams<TData>>): IUseQueryResponse<TData, TError> {
    const [queryStatus, setQueryStatus] = useState<IQueryStatus<TData, TError>>({
        isLoading: false,
        error: null,
        data: null
    });

    const abortControllerRef = useRef<AbortController | null>(null);

    async function fetchData(): Promise<IQueryStatus<TData, TError>> {
        abortControllerRef.current?.abort();
        const abortController = new AbortController();
        abortControllerRef.current = abortController;
        const signal = abortController.signal;

        setQueryStatus(prev => ({ ...prev, isLoading: true, error: null }));

        try {
            const data = await query(signal);
            if (!signal.aborted) {
                const queryStatus = { data, isLoading: false, error: null };
                setQueryStatus(queryStatus);
                return queryStatus;
            }
        }
        catch (error: any) {
            if (!signal.aborted) {
                console.error("Error with query:", error);
                const errorStatus = { data: null, error, isLoading: false };
                setQueryStatus(errorStatus);
                return errorStatus;
            }
        }

        return { isLoading: false, error: null, data: null };
    }

    useEffect(() => {
        fetchData();

        return () => {
            abortControllerRef.current?.abort();
        };
    }, queryKey);

    return {
        ...queryStatus,
        isError: !!queryStatus.error,
        isSuccess: queryStatus.error == null && !queryStatus.isLoading && queryStatus.data != null,
        refetch: fetchData
    };
}

export {
    IQueryStatus,
    IUseQueryParams,
    IUseQueryResponse,
    useQuery
};
